import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import GradientBackground from '@/components/ui/GradientBackground';
import TextReveal from '@/components/ui/TextReveal';
import { motion } from 'framer-motion';

const TermsOfServicePage = () => {
  return (
    <div className="bg-background text-foreground relative min-h-screen overflow-hidden">
      <GradientBackground />

      <div className="container mx-auto py-32 px-4 max-w-5xl relative z-10">
        {/* Header */}
        <div className="text-center mb-24">
          <div className="inline-block px-4 py-1.5 rounded-full border border-primary/30 bg-primary/10 backdrop-blur-md mb-6">
            <span className="text-primary text-sm font-semibold tracking-wider uppercase">Legal</span>
          </div>
          <TextReveal className="text-5xl md:text-8xl font-black tracking-tighter text-foreground leading-[0.9] mb-8">
            TERMS OF SERVICE.
          </TextReveal>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed"
          >
            The rules of engagement for the K&C Logistics neural network.
          </motion.p>
          <p className="text-sm text-muted-foreground mt-4 uppercase tracking-widest font-medium">Last Updated: December 15, 2025</p>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.8 }}
        >
          <Card className="bg-foreground/5 border-foreground/10 backdrop-blur-xl overflow-hidden shadow-2xl">
            <CardContent className="p-0">
              <ScrollArea className="h-[800px] p-8 md:p-12">
                <div className="space-y-12 text-base md:text-lg leading-relaxed text-muted-foreground">

                  <section>
                    <h2 className="text-3xl font-bold text-foreground mb-6">1. Introduction and Acceptance</h2>
                    <p className="mb-4">
                      Welcome to <strong className="text-foreground">K&C Logistics</strong> ("Company", "we", "us", or "our"). These Terms and Conditions ("Terms") govern your access to and use of our website, mobile application, and logistics platform (collectively, the "Platform").
                    </p>
                    <p className="mb-4">
                      By registering for, accessing, or using our Platform, you ("User", "you") agree to be bound by these Terms, our Privacy Policy, and any other guidelines or policies we may publish. If you do not agree to these Terms, you must not access or use the Platform.
                    </p>
                    <p>
                      These Terms constitute a legally binding agreement between you and K&C Logistics, a company registered and operating under the laws of the <strong className="text-foreground">Republic of Malawi</strong>.
                    </p>
                  </section>

                  <Separator className="bg-foreground/10" />

                  <section>
                    <h2 className="text-3xl font-bold text-foreground mb-6">2. Definitions</h2>
                    <ul className="list-disc pl-5 space-y-3 marker:text-primary">
                      <li><strong className="text-foreground">"Platform"</strong>: The K&C Logistics digital marketplace connecting Clients with Transporters, Fleet Managers, and Fleet Drivers.</li>
                      <li><strong className="text-foreground">"Client"</strong>: A User who posts cargo delivery jobs ("Jobs") on the Platform.</li>
                      <li><strong className="text-foreground">"Transporter"</strong>: An independent User (individual driver or fleet owner) who accepts Jobs to transport cargo.</li>
                      <li><strong className="text-foreground">"Fleet Manager"</strong>: A User who manages multiple vehicles and drivers, secures jobs, and assigns them to Fleet Drivers within their organization.</li>
                      <li><strong className="text-foreground">"Fleet Driver"</strong>: A User who operates as part of a fleet managed by a Fleet Manager, receiving job assignments and completing deliveries on behalf of the fleet.</li>
                      <li><strong className="text-foreground">"Services"</strong>: The facilitation of logistics coordination, payment processing, and shipment tracking provided by the Platform.</li>
                      <li><strong className="text-foreground">"Cargo"</strong>: The goods, merchandise, or materials entrusted by a Client to a Transporter, Fleet Manager, or Fleet Driver for shipment.</li>
                      <li><strong className="text-foreground">"Content"</strong>: Any text, images, data, or other materials uploaded, posted, or transmitted through the Platform.</li>
                    </ul>
                  </section>

                  <Separator className="bg-foreground/10" />

                  <section>
                    <h2 className="text-3xl font-bold text-foreground mb-6">3. Eligibility & Registration</h2>
                    <h3 className="text-xl font-bold text-foreground mb-3">3.1 Eligibility</h3>
                    <p className="mb-4">
                      You must be at least 18 years of age and possess the legal capacity to enter into binding contracts under the laws of Malawi. By using the Platform, you represent and warrant that you meet these eligibility requirements and that all information you provide is accurate and complete.
                    </p>
                    <h3 className="text-xl font-bold text-foreground mb-3">3.2 Account Security</h3>
                    <p className="mb-4">
                      You are solely responsible for maintaining the confidentiality of your account credentials, including your password. You agree to notify us immediately of any unauthorized use of your account or any other breach of security. K&C Logistics shall not be liable for any loss or damage arising from your failure to comply with this security obligation.
                    </p>
                    <h3 className="text-xl font-bold text-foreground mb-3">3.3 Verification</h3>
                    <p>
                      Transporters, Fleet Managers, and Fleet Drivers are subject to a mandatory verification process, which may include the submission of National IDs, Driver's Licenses, Vehicle Registration, Proof of Ownership, company documentation, and insurance documentation. We reserve the right to suspend, terminate, or deny access to unverified accounts at our sole discretion.
                    </p>
                  </section>

                  <Separator className="bg-foreground/10" />

                  <section>
                    <h2 className="text-3xl font-bold text-foreground mb-6">4. User Obligations</h2>

                    <h3 className="text-xl font-bold text-foreground mb-3">4.1 Client Obligations</h3>
                    <ul className="list-disc pl-5 space-y-2 marker:text-primary mb-6">
                      <li>You warrant that you are the legal owner of the Cargo or have the authority to ship it.</li>
                      <li>You must provide accurate details regarding the Cargo's weight, dimensions, nature, and value.</li>
                      <li>You agree not to ship prohibited, illegal, hazardous, or dangerous materials without proper authorization, disclosure, and compliance with applicable laws.</li>
                      <li>You are responsible for ensuring the Cargo is properly packed, labeled, and ready for pickup at the agreed time and location.</li>
                      <li>You must ensure that the delivery address is accurate and accessible.</li>
                    </ul>

                    <h3 className="text-xl font-bold text-foreground mb-3">4.2 Transporter Obligations</h3>
                    <ul className="list-disc pl-5 space-y-2 marker:text-primary mb-6">
                      <li>You must hold a valid driver's license and all necessary permits to operate your vehicle commercially.</li>
                      <li>You must maintain your vehicle in a safe, roadworthy, and insured condition at all times.</li>
                      <li>You agree to transport the Cargo safely, securely, and in a timely manner to the designated delivery address.</li>
                      <li>You consent to <strong className="text-foreground">real-time location tracking</strong> while a Job is "In Transit" for transparency, safety, and customer service purposes.</li>
                      <li>You must comply with all applicable traffic laws, regulations, and safety standards.</li>
                      <li>You are responsible for obtaining and maintaining appropriate insurance coverage for your vehicle and cargo.</li>
                    </ul>

                    <h3 className="text-xl font-bold text-foreground mb-3">4.3 Fleet Manager Obligations</h3>
                    <ul className="list-disc pl-5 space-y-2 marker:text-primary mb-6">
                      <li>You must register all vehicles under your fleet and ensure they meet safety and legal requirements.</li>
                      <li>You are responsible for verifying and managing your Fleet Drivers, including ensuring they have valid licenses and proper training.</li>
                      <li>You must assign jobs to appropriate drivers based on availability, location, and capability.</li>
                      <li>You are responsible for all payments to your Fleet Drivers and must maintain transparent payment records.</li>
                      <li>You consent to real-time tracking of all vehicles and drivers under your fleet during active jobs.</li>
                      <li>You must ensure all vehicles in your fleet are properly insured and maintained.</li>
                    </ul>

                    <h3 className="text-xl font-bold text-foreground mb-3">4.4 Fleet Driver Obligations</h3>
                    <ul className="list-disc pl-5 space-y-2 marker:text-primary">
                      <li>You must hold a valid driver's license and comply with all requirements set by your Fleet Manager.</li>
                      <li>You agree to accept job assignments from your Fleet Manager and complete them professionally.</li>
                      <li>You consent to <strong className="text-foreground">mandatory, automatic location tracking</strong> that starts when you begin a trip and stops upon delivery completion.</li>
                      <li>You must transport Cargo safely and deliver it to the correct destination in good condition.</li>
                      <li>You acknowledge that all payments for jobs you complete are managed by your Fleet Manager.</li>
                      <li>You must maintain the assigned vehicle in good condition and report any issues immediately.</li>
                    </ul>
                  </section>

                  <Separator className="bg-foreground/10" />

                  <section>
                    <h2 className="text-3xl font-bold text-foreground mb-6">5. Financial Terms</h2>
                    <h3 className="text-xl font-bold text-foreground mb-3">5.1 Booking Fee</h3>
                    <p className="mb-4">
                      Transporters are required to pay a non-refundable <strong className="text-foreground">Booking Fee of MWK 50,000 per load</strong> to secure a Job on the Platform. This fee is paid to K&C Logistics via PayChangu or Wallet balance and constitutes a service charge for access to the marketplace and platform services.
                    </p>

                    <h3 className="text-xl font-bold text-foreground mb-3">5.2 Direct Settlement</h3>
                    <p className="mb-4">
                      The Platform facilitates connections but does not currently process cargo shipping payments. <strong className="text-foreground">Clients must pay Transporters directly</strong> (e.g., via Cash, Bank Transfer, or Mobile Money) upon successful delivery of the Cargo.
                    </p>
                    <p className="mb-4">
                      K&C Logistics is not a party to the financial transaction between Client and Transporter regarding the shipping cost and is not liable for any non-payment, disputes, or issues arising from this direct settlement arrangement.
                    </p>

                    <h3 className="text-xl font-bold text-foreground mb-3">5.3 Payment Disputes</h3>
                    <p>
                      Any payment disputes between Clients and Transporters must be resolved directly between the parties. While we may provide assistance in facilitating communication, we are not obligated to intervene in payment disputes and bear no liability for the outcome.
                    </p>
                  </section>

                  <Separator className="bg-foreground/10" />

                  <section>
                    <h2 className="text-3xl font-bold text-foreground mb-6">6. Prohibited Conduct</h2>
                    <p className="mb-4">You agree not to:</p>
                    <ul className="list-disc pl-5 space-y-2 marker:text-primary">
                      <li>Use the Platform for any illegal or unauthorized purpose.</li>
                      <li>Violate any laws in your jurisdiction (including but not limited to copyright laws).</li>
                      <li>Transmit any viruses, malware, or other malicious code.</li>
                      <li>Attempt to gain unauthorized access to any portion of the Platform or any other systems or networks.</li>
                      <li>Engage in any conduct that restricts or inhibits anyone's use or enjoyment of the Platform.</li>
                      <li>Impersonate any person or entity or falsely state or misrepresent your affiliation with a person or entity.</li>
                      <li>Interfere with or disrupt the Platform or servers or networks connected to the Platform.</li>
                      <li>Collect or store personal data about other users without their express consent.</li>
                      <li>Use automated systems or software to extract data from the Platform ("scraping").</li>
                    </ul>
                  </section>

                  <Separator className="bg-foreground/10" />

                  <section>
                    <h2 className="text-3xl font-bold text-foreground mb-6">7. Intellectual Property</h2>
                    <p className="mb-4">
                      The Platform and its entire contents, features, and functionality (including but not limited to all information, software, text, displays, images, video, and audio, and the design, selection, and arrangement thereof) are owned by K&C Logistics, its licensors, or other providers of such material and are protected by Malawi and international copyright, trademark, patent, trade secret, and other intellectual property or proprietary rights laws.
                    </p>
                    <p className="mb-4">
                      You are granted a limited, non-exclusive, non-transferable, and revocable license to access and use the Platform for your personal or business use in accordance with these Terms. You may not reproduce, distribute, modify, create derivative works of, publicly display, publicly perform, republish, download, store, or transmit any of the material on our Platform without our prior written consent.
                    </p>
                  </section>

                  <Separator className="bg-foreground/10" />

                  <section>
                    <h2 className="text-3xl font-bold text-foreground mb-6">8. Limitation of Liability</h2>
                    <p className="mb-4">
                      K&C Logistics acts solely as an intermediary marketplace connecting Clients with Transporters. We do not provide transportation services ourselves and are not a common carrier. We do not guarantee the performance, reliability, or quality of any Transporter or the integrity, honesty, or safety of any Client.
                    </p>
                    <p className="mb-4">
                      To the fullest extent permitted by applicable law, in no event shall K&C Logistics, its officers, directors, employees, agents, or affiliates be liable for any indirect, incidental, special, consequential, or punitive damages, including but not limited to loss of profits, data, use, goodwill, or other intangible losses, resulting from:
                    </p>
                    <ul className="list-disc pl-5 space-y-2 marker:text-primary mb-4">
                      <li>Your access to or use of or inability to access or use the Platform;</li>
                      <li>Any conduct or content of any third party on the Platform;</li>
                      <li>Any content obtained from the Platform;</li>
                      <li>Unauthorized access, use, or alteration of your transmissions or content;</li>
                      <li>Loss, damage, or delay of cargo;</li>
                      <li>Personal injury or property damage arising from your use of the Platform or services;</li>
                      <li>Any errors, mistakes, or inaccuracies of content;</li>
                    </ul>
                    <p>
                      Our total liability to you for all claims arising out of or related to these Terms or the Platform shall not exceed the amount you paid to us in the twelve (12) months preceding the claim, or MWK 100,000, whichever is less.
                    </p>
                  </section>

                  <Separator className="bg-foreground/10" />

                  <section>
                    <h2 className="text-3xl font-bold text-foreground mb-6">9. Indemnification</h2>
                    <p className="mb-4">
                      You agree to defend, indemnify, and hold harmless K&C Logistics, its officers, directors, employees, agents, licensors, and suppliers from and against any and all claims, damages, obligations, losses, liabilities, costs, debt, and expenses (including but not limited to attorney's fees) arising from:
                    </p>
                    <ul className="list-disc pl-5 space-y-2 marker:text-primary">
                      <li>Your use of and access to the Platform;</li>
                      <li>Your violation of any term of these Terms;</li>
                      <li>Your violation of any third-party right, including without limitation any copyright, property, or privacy right;</li>
                      <li>Any claim that your Content caused damage to a third party;</li>
                      <li>Your transportation or shipment of Cargo;</li>
                      <li>Any disputes between you and other users of the Platform.</li>
                    </ul>
                  </section>

                  <Separator className="bg-foreground/10" />

                  <section>
                    <h2 className="text-3xl font-bold text-foreground mb-6">10. Termination</h2>
                    <p className="mb-4">
                      We may terminate or suspend your account and bar access to the Platform immediately, without prior notice or liability, under our sole discretion, for any reason whatsoever and without limitation, including but not limited to a breach of these Terms.
                    </p>
                    <p className="mb-4">
                      If you wish to terminate your account, you may simply discontinue using the Platform or contact us to request account deletion.
                    </p>
                    <p>
                      All provisions of these Terms which by their nature should survive termination shall survive termination, including, without limitation, ownership provisions, warranty disclaimers, indemnity, and limitations of liability.
                    </p>
                  </section>

                  <Separator className="bg-foreground/10" />

                  <section>
                    <h2 className="text-3xl font-bold text-foreground mb-6">11. Modifications to Terms</h2>
                    <p className="mb-4">
                      We reserve the right, at our sole discretion, to modify or replace these Terms at any time. If a revision is material, we will provide at least 30 days' notice prior to any new terms taking effect. What constitutes a material change will be determined at our sole discretion.
                    </p>
                    <p>
                      By continuing to access or use our Platform after any revisions become effective, you agree to be bound by the revised terms. If you do not agree to the new terms, you are no longer authorized to use the Platform.
                    </p>
                  </section>

                  <Separator className="bg-foreground/10" />

                  <section>
                    <h2 className="text-3xl font-bold text-foreground mb-6">12. Governing Law and Dispute Resolution</h2>
                    <p className="mb-4">
                      These Terms shall be governed and construed in accordance with the laws of the <strong className="text-foreground">Republic of Malawi</strong>, without regard to its conflict of law provisions.
                    </p>
                    <p className="mb-4">
                      Any dispute arising out of or in connection with these Terms, including any question regarding their existence, validity, or termination, shall first be attempted to be resolved through good-faith negotiations between the parties.
                    </p>
                    <p>
                      If the dispute cannot be resolved through negotiation within thirty (30) days, it shall be referred to and finally resolved by arbitration in accordance with the Arbitration Act of Malawi. The seat of the arbitration shall be Lilongwe, Malawi. The language of the arbitration shall be English.
                    </p>
                  </section>

                  <Separator className="bg-foreground/10" />

                  <section>
                    <h2 className="text-3xl font-bold text-foreground mb-6">13. Severability</h2>
                    <p>
                      If any provision of these Terms is held to be invalid or unenforceable by a court, the remaining provisions of these Terms will remain in effect. These Terms constitute the entire agreement between us regarding our Platform and supersede and replace any prior agreements we might have had between us regarding the Platform.
                    </p>
                  </section>

                  <Separator className="bg-foreground/10" />

                  <section>
                    <h2 className="text-3xl font-bold text-foreground mb-6">14. Contact Information</h2>
                    <p className="mb-4">
                      If you have any questions about these Terms, please contact us at:
                    </p>
                    <div className="p-6 bg-background/30 rounded-2xl border border-foreground/5 backdrop-blur-md">
                      <p className="text-foreground font-bold mb-2">K&C Logistics Legal Team</p>
                      <ul className="space-y-1 text-sm">
                        <li>Email: legal@kclogistics.mw</li>
                        <li>Phone: +265 999 123 456</li>
                        <li>Address: City Centre, Lilongwe, Malawi</li>
                      </ul>
                    </div>
                  </section>

                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
};

export default TermsOfServicePage;